import React, { useRef } from 'react';
import { toast } from 'sonner';
import { FileUp, Check, X, Trash2 } from 'lucide-react';
import { useFreehand } from '@/lib/freehandStore';
import { parseSvgText } from '@/lib/svgImport';

const SIDES = ['front', 'back', 'left', 'right', 'top', 'bottom'];

const RangeRow = ({ label, value, min, max, onChange, suffix, testId }) => (
  <div className="text-[11px] text-slate-500 flex items-center gap-2">
    <span className="w-9">{label}</span>
    <input type="range" min={min} max={max} step={1} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1" data-testid={testId} />
    <span className="w-9 tabular-nums text-right">{value}{suffix}</span>
  </div>
);

export const FreehandSvgImport = () => {
  const draft = useFreehand((s) => s.svgDraft);
  const guideCount = useFreehand((s) => s.svgGuides.length);
  const setDraft = useFreehand((s) => s.setSvgDraft);
  const update = useFreehand((s) => s.updateSvgDraft);
  const place = useFreehand((s) => s.placeSvgDraft);
  const clearGuides = useFreehand((s) => s.clearSvgGuides);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { closed, open } = parseSvgText(await file.text());
      if (!closed.length && !open.length) throw new Error('No vector shapes found in this SVG');
      setDraft({ closed, open, pivot: 'front', sizeDeg: 70, spinDeg: 0 });
      toast.success(`Found ${closed.length} panel${closed.length === 1 ? '' : 's'} and ${open.length} guide line${open.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.message || 'Could not read that SVG');
    }
  };

  const onPlace = () => {
    const n = draft.closed.length;
    place();
    toast.success(`Placed ${n} panel${n === 1 ? '' : 's'} on the ball`);
  };

  return (
    <div className="p-3 border-b border-slate-200 space-y-2" data-testid="freehand-svg-import">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">Import SVG</div>
      <input ref={inputRef} type="file" accept=".svg,image/svg+xml" onChange={onFile} className="hidden" data-testid="freehand-svg-input" />
      {!draft ? (
        <>
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="freehand-svg-upload"
            className="w-full rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300 flex items-center justify-center gap-1.5"
          >
            <FileUp className="w-3.5 h-3.5" /> Upload SVG
          </button>
          {guideCount > 0 && (
            <button onClick={clearGuides} data-testid="freehand-svg-clear-guides" className="w-full rounded-lg text-[11px] py-1.5 text-red-600 border border-red-200 hover:bg-red-50 flex items-center justify-center gap-1.5">
              <Trash2 className="w-3 h-3" /> Clear {guideCount} tracing guide{guideCount === 1 ? '' : 's'}
            </button>
          )}
        </>
      ) : (
        <>
          <div className="text-[11px] text-slate-600" data-testid="freehand-svg-summary">
            {draft.closed.length} panels · {draft.open.length} guide lines. Shapes inside shapes are punched out.
          </div>
          <div className="grid grid-cols-3 gap-1">
            {SIDES.map((id) => (
              <button
                key={id}
                onClick={() => update({ pivot: id })}
                data-testid={`freehand-svg-side-${id}`}
                className={`h-6 rounded-md border text-[10px] capitalize transition-colors ${draft.pivot === id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-900'}`}
              >
                {id}
              </button>
            ))}
          </div>
          <RangeRow label="Size" value={draft.sizeDeg} min={10} max={180} suffix="°" onChange={(v) => update({ sizeDeg: v })} testId="freehand-svg-size" />
          <RangeRow label="Spin" value={draft.spinDeg} min={-180} max={180} suffix="°" onChange={(v) => update({ spinDeg: v })} testId="freehand-svg-spin" />
          <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" checked={draft.punch !== false} onChange={(e) => update({ punch: e.target.checked })} data-testid="freehand-svg-punch" />
            Cut out panels underneath
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={onPlace} data-testid="freehand-svg-place" className="h-7 rounded-lg text-[11px] font-medium bg-slate-900 text-white flex items-center justify-center gap-1">
              <Check className="w-3 h-3" /> Place
            </button>
            <button onClick={() => setDraft(null)} data-testid="freehand-svg-cancel" className="h-7 rounded-lg text-[11px] border border-slate-200 bg-white hover:border-slate-900 flex items-center justify-center gap-1">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};
